import { gql } from '@apollo/client';

/**
 * PUBLIC (no auth) — the form token in the URL is the authorization. The Apollo
 * refresh-on-UNAUTHENTICATED link never fires for these because the resolver is
 * unguarded.
 */
export const GET_ONBOARDING_FORM_BY_TOKEN_QUERY = gql`
    query GetOnboardingFormByToken($formToken: String!) {
        getOnboardingFormByToken(formToken: $formToken) {
            businessId
            status
            questionnaireVersion
            questionnaire
            answers
        }
    }
`;

/** PUBLIC (no auth) — the checklist token in the URL is the authorization. */
export const GET_CHECKLIST_BY_TOKEN_QUERY = gql`
    query GetChecklistByToken($checklistToken: String!) {
        getChecklistByToken(checklistToken: $checklistToken) {
            businessId
            businessName
            status
            intro
            progress {
                total
                done
            }
            sections {
                key
                title
                badge
                phase
                prerequisites
                items {
                    id
                    label
                    note
                    optional
                    phase
                    done
                }
            }
            comments {
                _id
                itemId
                authorType
                authorName
                text
                parentId
                createdAt
            }
        }
    }
`;

/** Staff (@Auth) — the comment feed for a business's manual. */
export const GET_CHECKLIST_COMMENTS_QUERY = gql`
    query GetChecklistComments($manualId: String!) {
        getChecklistComments(manualId: $manualId) {
            _id
            itemId
            authorType
            authorName
            text
            parentId
            createdAt
        }
    }
`;

const TEMPLATE_FIELDS = `
    variant
    intro
    customised
    published
    revision
    publishedAt
    seedVersion
    sections {
        key
        title
        badge
        phase
        prerequisites
        items {
            id
            label
            note
            optional
            requiredIfService
            optionalFeatureKey
        }
    }
`;

/** Staff (@Auth) — the CMS-editable onboarding questionnaire (singleton). */
export const GET_ONBOARDING_QUESTIONNAIRE_QUERY = gql`
    query GetOnboardingQuestionnaire {
        getOnboardingQuestionnaire {
            definition
            customised
            published
            revision
            publishedAt
            seedVersion
        }
    }
`;

/** Staff (@Auth) — the three CMS-editable master setup-manual templates. */
export const LIST_ONBOARDING_TEMPLATES_QUERY = gql`
    query ListOnboardingTemplates {
        listOnboardingTemplates {
            variant
            customised
            published
            revision
            publishedAt
            updatedAt
            seedVersion
        }
    }
`;

export const GET_ONBOARDING_TEMPLATE_QUERY = gql`
    query GetOnboardingTemplate($variant: String!) {
        getOnboardingTemplate(variant: $variant) {
            ${TEMPLATE_FIELDS}
        }
    }
`;

/** Staff (@Auth) — CSM dashboard: every business with onboarding in flight. */
export const LIST_ONBOARDING_QUERY = gql`
    query ListOnboarding($skip: Float, $limit: Float) {
        listOnboarding(skip: $skip, limit: $limit) {
            businessId
            businessName
            submissionStatus
            manualStatus
            checklistStatus
            checklistProgressPct
            progress {
                total
                done
            }
        }
    }
`;

export const GET_ONBOARDING_QUERY = gql`
    query GetOnboarding($businessId: String!) {
        getOnboarding(businessId: $businessId) {
            businessId
            submission {
                _id
                status
                formToken
                formUrl
                formShortUrl
                answers
                sentAt
                submittedAt
            }
            manual {
                _id
                status
                templateVariant
                templateVersion
                intro
                checklistToken
                checklistUrl
                checklistShortUrl
                sentAt
                sections {
                    key
                    title
                    badge
                    phase
                    prerequisites
                    items {
                        id
                        label
                        note
                        optional
                        phase
                        done
                    }
                }
            }
            progress {
                total
                done
            }
        }
    }
`;
